import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const JiraTaskModal = ({ isOpen, onClose, initialSummary, initialDescription, projectName }) => {
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [jiraConfigured, setJiraConfigured] = useState(null);
  const [checkingConfig, setCheckingConfig] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSummary(initialSummary || '');
      setDescription(initialDescription || '');
      setResult(null);
      setError(null);
      checkJiraConfig();
    }
  }, [isOpen, initialSummary, initialDescription, projectName]);

  const checkJiraConfig = async () => {
    setCheckingConfig(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(
        `${API_URL}/jira/config/${encodeURIComponent(projectName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJiraConfigured(res.data.configured);
    } catch {
      setJiraConfigured(false);
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleSend = async () => {
    if (!summary.trim()) {
      setError('Subject is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.post(
        `${API_URL}/jira/create-issue`,
        { projectName, summary: summary.trim(), description: description.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create Jira issue');
    } finally {
      setLoading(false);
    }
  };

  const handleSummaryChange = (e) => {
    const text = e.target.value;
    
    const titleMatch = text.match(/\[Title\]:\s*(.*?)(?=\s*\[Description\]:|$)/i);
    const descMatch = text.match(/\[Description\]:\s*([\s\S]*)$/i);

    if (titleMatch || descMatch) {
      if (titleMatch) setSummary(titleMatch[1].trim());
      if (descMatch) setDescription(descMatch[1].trim());
    } else {
      setSummary(text);
    }
  };

  const handleDescriptionChange = (e) => {
    const text = e.target.value;
    
    const titleMatch = text.match(/\[Title\]:\s*(.*?)(?=\s*\[Description\]:|$)/i);
    const descMatch = text.match(/\[Description\]:\s*([\s\S]*)$/i);

    if (titleMatch || descMatch) {
      if (titleMatch) setSummary(titleMatch[1].trim());
      if (descMatch) setDescription(descMatch[1].trim());
    } else {
      setDescription(text);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900/50 dark:bg-gray-900/80" onClick={onClose}>
      <div className="relative w-full max-w-3xl p-4 max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-800 flex flex-col max-h-[calc(90vh-2rem)]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-blue-600 dark:text-blue-400">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Jira Task</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" /></svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4 overflow-y-auto">
            {!checkingConfig && !jiraConfigured && (
              <div className="flex items-center gap-2 p-3 text-sm text-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <span>Populate Jira secrets for this project to enable ticket creation</span>
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-700 dark:text-red-400">{error}</div>
            )}

            {result && (
              <div className="p-3 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-700 dark:text-green-400">
                Issue <a href={result.url} target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:no-underline">{result.key}</a> created successfully!
              </div>
            )}

            <div>
              <label htmlFor="jira-summary" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Subject *</label>
              <input
                type="text"
                id="jira-summary"
                value={summary}
                onChange={handleSummaryChange}
                placeholder="Issue subject (Paste text with [Title]: and [Description]: to auto-extract)"
                disabled={!!result}
                className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="jira-description" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Description</label>
              <textarea
                id="jira-description"
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Issue description (Paste text with [Title]: and [Description]: to auto-extract)"
                rows={6}
                disabled={!!result}
                className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 disabled:opacity-60 resize-y"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 rounded-b dark:border-gray-600">
            {result ? (
              <button
                type="button"
                onClick={onClose}
                className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!jiraConfigured || loading || checkingConfig}
                  className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraTaskModal;
